import type { Route } from "./+types/recipes";
import { brandName } from "@mealmanager/legal-content";
import Page from "~/recipes/page";
import gqlClientConnect from "src/api-config/graphql.config";
import {
  SearchRecipesDocument,
  type Recipe,
  type SearchRecipesQuery,
  type SearchRecipesQueryVariables,
} from "src/graphql/generated/operations";
import { fetchConfigForRequest } from "src/app/utils/configLoader";
import type { NormalizedCacheObject } from "@apollo/client";
import { buildSeoMeta } from "../utils/seo";
import { buildFilter, buildSearchInput } from "./recipes.helpers";

const TRACKING_PARAMETERS = new Set(["gclid", "fbclid", "msclkid", "gbraid", "wbraid", "gad_source"]);

export const meta: Route.MetaFunction = ({ location }) => {
  const searchParams = new URLSearchParams(location.search);
  const hasFunctionalQuery = Array.from(searchParams.keys()).some((key) =>
    !key.toLowerCase().startsWith("utm_") && !TRACKING_PARAMETERS.has(key.toLowerCase())
  );
  const filter = buildFilter(searchParams);
  const category = filter?.category;
  const type = filter?.type;
  const ingredientFilterApplied = searchParams.has("hasIngredients");
  const tagFilterApplied = searchParams.has("cuisineTags") || searchParams.has("customTags");

  const filterSummary: string[] = [];
  if (category) {
    filterSummary.push(`${category.toLowerCase()} recipes`);
  }
  if (type) {
    filterSummary.push(`${type.toLowerCase()} friendly options`);
  }
  if (ingredientFilterApplied) {
    filterSummary.push("matching ingredients you already have");
  }
  if (tagFilterApplied) {
    filterSummary.push("tag-based suggestions");
  }

  const titleContext =
    category || type ? `${[category, type].filter(Boolean).join(" ")} Recipes | ${brandName}` : `Recipes for Every Plan | ${brandName}`;
  const description = filterSummary.length
    ? `Browse curated ${filterSummary.join(", ")}. Adjust macros, ratings, cuisine tags, and more before sending meals to your plan or grocery list.`
    : `Browse curated ${brandName} recipes, filter by cuisine, macro profile, pantry ingredients, or rating, and send favourites straight to your weekly meal plan.`;

  return buildSeoMeta({
    location,
    title: titleContext,
    description,
    noindex: hasFunctionalQuery,
    follow: true,
    includeCanonical: !hasFunctionalQuery,
  });
};

type LoaderData = {
  apolloState: NormalizedCacheObject | null;
  recipes: Recipe[] | null;
};

export async function loader({ request, context }: Route.LoaderArgs) {
  const config = await fetchConfigForRequest(request, context);
  if (!config) {
    const payload: LoaderData = { apolloState: null, recipes: null };
    return Response.json(payload);
  }
  const client = gqlClientConnect(config.httpURL);
  const url = new URL(request.url);
  const input = buildSearchInput(url.searchParams);
  const { data } = await client.query<SearchRecipesQuery, SearchRecipesQueryVariables>({
    query: SearchRecipesDocument,
    variables: { input },
  });
  const apolloState = client.extract() as NormalizedCacheObject;
  const payload: LoaderData = {
    apolloState,
    recipes: data?.searchRecipes.recipes ?? null,
  };
  return Response.json(payload);
}

export default function Recipes() {
  return <Page />;
}
